import {AxiosError, AxiosResponse} from "axios";

import {NotFoundError} from "./NotFound";
import {QueryError} from "./QueryError";

export const handleRequest = <T>(axiosRequest: () => Promise<AxiosResponse<T>>): Promise<T> => {
    return axiosRequest()
        .then((response) => {
            return response.data;
        })
        .catch((error) => {
            // Handle network errors first (no response available)
            if (error instanceof AxiosError && error.code === "ERR_NETWORK") {
                throw new QueryError(
                    "Chyba sítě - nelze se připojit k serveru.",
                    "Zkontrolujte připojení k internetu",
                    0, // Use 0 for network errors instead of misleading 500
                    error.code
                );
            }

            // Handle timeout errors
            if (error instanceof AxiosError && error.code === "ECONNABORTED") {
                throw new QueryError(
                    "Požadavek trval příliš dlouho.",
                    "Server neodpověděl včas. Operace mohla proběhnout úspěšně - zkontrolujte výsledek.",
                    408, // Request Timeout
                    error.code
                );
            }

            if (error.response && error.response.status === 404) {
                throw new NotFoundError("404 - Nenalezeno", "Cause");
            }

            // Handle 401 Unauthorized
            if (error.response && error.response.status === 401) {
                throw new QueryError(
                    "Nesprávné přihlašovací údaje.",
                    "Zkontrolujte email a heslo",
                    401
                );
            }

            // 413 chodi z reverzni proxy (nginx client_max_body_size), ne z aplikace,
            // takze telo je HTML — ani `message`, ani `error` v nem nejsou a obecna
            // vetev nize by z toho udelala nicnerikajici "Vnitrni chyba serveru.".
            if (error.response && error.response.status === 413) {
                throw new QueryError(
                    "Soubor je příliš velký pro server.",
                    "Odmítla ho reverzní proxy dřív, než se dostal k aplikaci. Zkuste menší soubor; pokud se to opakuje i u běžných fotek, je potřeba zvýšit limit na serveru.",
                    413
                );
            }

            if (error.response && error.response.data) {
                const errorMessage = error.response.data.message
                    || error.response.data.error
                    || "Vnitřní chyba serveru.";
                const errorDetail = error.response.data.detail || "Neznámá příčina";
                const errorCode = error.response.status || 500;
                // `kod` (HO-148/HO-151): strojovy kod chybove obalky (napr. "vlakno_uzamceno"
                // u 423, "konflikt_..." u 409) - viz GlobalExceptionHandler#chyba na BE.
                // `message` zustava beze zmeny, jak rika HO-143 - kod se jen prida vedle.
                const kod = error.response.data.kod;
                throw new QueryError(errorMessage, errorDetail, errorCode, kod);
            }

            // Fallback for other errors
            throw new QueryError(
                error.message || "Vnitřní chyba serveru.",
                "Neznámá příčina",
                error.response?.status || 500
            );
        });
};
